import { useCallback, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { onboardingQuestionnaireActions } from '../../../../../../store';
import { getUtcOffset } from '../../../../../../utils/common';
import { ONBOARDING_TAB_CONFIGS, ONBOARDING_TAB_IDS } from '../../../constants';
import useOnboardingTabStatuses from '../../../hooks/useOnboardingTabStatuses';
import { useInsuranceExtractionMap } from '../../forms/insurance/hooks';
import { CONCLUDE_REQUIRED_TAB_IDS } from '../constants';
import { getConcludeErrorPageLabel, isExtractionBlockingTab } from './utils';

import type { AppDispatch, RootState } from '../../../../../../store';
import type { ConcludeOnboardingQuestionnaireDependencies } from './types';

export default function useConcludeOnboardingQuestionnaire() {
  const dispatch = useDispatch<AppDispatch>();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();
  const { getAccessTokenSilently } = useAuth0();
  const tabStatuses = useOnboardingTabStatuses();
  const extraction = useInsuranceExtractionMap();
  const isSubmitting = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.submitQuestionnaireLoading,
  );
  const noSecondaryInsurance = useSelector(
    (state: RootState) =>
      state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.no_secondary_insurance ?? false,
  );

  return useConcludeOnboardingQuestionnaireWithDependencies({
    actions: onboardingQuestionnaireActions,
    dispatch,
    extraction,
    getAccessTokenSilently,
    isSubmitting,
    noSecondaryInsurance,
    onboardingQuestionnaireId,
    tabStatuses,
  });
}

export function useConcludeOnboardingQuestionnaireWithDependencies({
  actions,
  dispatch,
  extraction,
  getAccessTokenSilently,
  isSubmitting,
  noSecondaryInsurance,
  onboardingQuestionnaireId,
  tabStatuses,
}: ConcludeOnboardingQuestionnaireDependencies) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  // Labels of pages that still block conclude, in tab order: incomplete required
  // forms, secondary insurance when the client has one and it is incomplete, any
  // tab with a schema error, and insurance tabs blocked by extraction/review.
  const errorPageLabels = useMemo(
    () =>
      ONBOARDING_TAB_CONFIGS.filter((tab) => {
        const status = tabStatuses[tab.id];
        const isRequiredIncomplete =
          CONCLUDE_REQUIRED_TAB_IDS.some((id) => id === tab.id) && !status.success;
        const isSecondaryRequiredIncomplete =
          tab.id === ONBOARDING_TAB_IDS.SECONDARY_INSURANCE &&
          !noSecondaryInsurance &&
          !status.success;
        return (
          isRequiredIncomplete ||
          isSecondaryRequiredIncomplete ||
          status.error ||
          isExtractionBlockingTab(tab.id, extraction)
        );
      }).map(getConcludeErrorPageLabel),
    [extraction, noSecondaryInsurance, tabStatuses],
  );

  const openConfirm = useCallback(() => setIsConfirmOpen(true), []);
  const closeConfirm = useCallback(() => setIsConfirmOpen(false), []);

  const confirmConclude = useCallback(async () => {
    if (!onboardingQuestionnaireId) return;

    try {
      await dispatch(
        actions.submitOnboardingQuestionnaire({
          onboardingQuestionnaireId,
          data: {
            date_concluded_utc_offset: getUtcOffset(moment()),
          },
          getAccessTokenSilently,
        }),
      ).unwrap();
      setIsConfirmOpen(false);
      setIsSuccessOpen(true);
    } catch {
      // Error toast is raised by the slice; keep the confirm modal open for retry.
    }
  }, [actions, dispatch, getAccessTokenSilently, onboardingQuestionnaireId]);

  const closeSuccess = useCallback(() => {
    setIsSuccessOpen(false);
    if (!onboardingQuestionnaireId) return;

    dispatch(
      actions.getOnboardingQuestionnaireById({
        onboardingQuestionnaireId,
        getAccessTokenSilently,
      }),
    );
  }, [actions, dispatch, getAccessTokenSilently, onboardingQuestionnaireId]);

  return {
    errorPageLabels,
    isSubmitting,
    isConfirmOpen,
    isSuccessOpen,
    openConfirm,
    closeConfirm,
    confirmConclude,
    closeSuccess,
  };
}
